// src/pages/AddListing.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { addListing } from '../services/ListingsService';
import { geocodeAddress } from '../services/GeocodingService';
import { uploadMultipleMedia } from '../services/StorageService';
import MultiMediaUpload from '../components/MultiMediaUpload';
import LocationAutocomplete from '../components/LocationAutocomplete';
import './Listings.css';

function AddListing() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geocodingError, setGeocodingError] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  
  const MAX_LOCATIONS = 5;

  const createEmptyLocation = () => ({
    id: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    address: '',
    coordinates: null
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    isVirtual: false,
    price: '',
    isFreeEvent: false,
    isContactForPrice: false,
    media: [], // Changed from imageUrl to media array
    contact: currentUser?.email || '',
    coordinates: null,
    maxAttendees: '',
    locations: [createEmptyLocation()],
    // Event dates
    eventStart: '', // datetime-local string
    eventEnd: '',   // datetime-local string (optional)
    // Social media fields
    socialMedia: {
      website: '',
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: ''
    }
  });
  const [locationErrors, setLocationErrors] = useState({});
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle social media fields
    if (name.startsWith('social_')) {
      const platform = name.replace('social_', '');
      setFormData(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [platform]: value
        }
      }));
      return;
    }
    
    // Handle checkbox inputs
    if (type === 'checkbox') {
      setFormData(prev => {
        const next = {
          ...prev,
          [name]: checked,
          ...(name === 'isVirtual' && checked ? { coordinates: null } : {})
        };
        if (name === 'isVirtual' && !checked && prev.locations?.length) {
          next.location = prev.locations[0].address || '';
          next.coordinates = prev.locations[0].coordinates || null;
        }
        return next;
      });
      
      // If isVirtual is being checked, reset location errors
      if (name === 'isVirtual' && checked) {
        setGeocodingError(null);
        setLocationErrors({});
      }
      
      // If isFreeEvent is checked, set price to 0 and uncheck isContactForPrice
      if (name === 'isFreeEvent' && checked) {
        setFormData(prev => ({
          ...prev,
          price: '0',
          isContactForPrice: false
        }));
      }
      
      // If isContactForPrice is checked, set price to 0 and uncheck isFreeEvent
      if (name === 'isContactForPrice' && checked) {
        setFormData(prev => ({
          ...prev,
          price: '0',
          isFreeEvent: false
        }));
      }
      
      return;
    }
    
    if (name === 'location') {
      setFormData(prev => ({
        ...prev,
        location: value,
        coordinates: null
      }));
      setGeocodingError(null);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If price is changed and not 0, uncheck both price-related checkboxes
    if (name === 'price' && value !== '0' && value !== '') {
      setFormData(prev => ({
        ...prev,
        isFreeEvent: false,
        isContactForPrice: false
      }));
    }
  };
  
  const updateLocationsState = (updater) => {
    setFormData(prev => {
      const nextLocations = updater(prev.locations || []);
      const ensuredLocations = nextLocations.length > 0 ? nextLocations : [createEmptyLocation()];
      const primary = ensuredLocations[0];
      return {
        ...prev,
        locations: ensuredLocations,
        location: prev.isVirtual ? prev.location : (primary.address || ''),
        coordinates: prev.isVirtual ? null : (primary.coordinates || null)
      };
    });
  };

  const handleLocationInputChange = (locationId, value) => {
    updateLocationsState(prevLocations =>
      prevLocations.map(loc =>
        loc.id === locationId ? { ...loc, address: value, coordinates: null } : loc
      )
    );
    setLocationErrors(prev => ({ ...prev, [locationId]: '' }));
    setGeocodingError(null);
  };
  
  const handleLocationSelect = (locationId, suggestion) => {
    updateLocationsState(prevLocations =>
      prevLocations.map(loc =>
        loc.id === locationId
          ? {
              ...loc,
              address: suggestion.displayName,
              coordinates: { lat: suggestion.lat, lng: suggestion.lng }
            }
          : loc
      )
    );
    setLocationErrors(prev => ({ ...prev, [locationId]: '' }));
    setGeocodingError(null);
  };
  
  const handleAddLocation = () => {
    updateLocationsState(prevLocations => {
      if (prevLocations.length >= MAX_LOCATIONS) {
        return prevLocations;
      }
      return [...prevLocations, createEmptyLocation()];
    });
  };
  
  const handleRemoveLocation = (locationId) => {
    updateLocationsState(prevLocations => {
      if (prevLocations.length === 1) return prevLocations;
      return prevLocations.filter(loc => loc.id !== locationId);
    });
    setLocationErrors(prev => {
      const next = { ...prev };
      delete next[locationId];
      return next;
    });
  };
  
  const handleMediaSelected = (files, previews) => {
    setMediaFiles(files);
    setUploadError(null);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setGeocodingError(null);
    setUploadError(null);

    try {
      // Validate event dates (both optional; ensure end is not before start if both provided)
      let startDate = null;
      if (formData.eventStart) {
        const parsedStart = new Date(formData.eventStart);
        if (!isNaN(parsedStart)) {
          startDate = parsedStart;
        }
      }
      let endDate = null;
      if (formData.eventEnd) {
        const parsedEnd = new Date(formData.eventEnd);
        if (!isNaN(parsedEnd)) {
          endDate = parsedEnd;
        }
        if (startDate && endDate && endDate < startDate) {
          alert('Event end must be after the start.');
          setIsSubmitting(false);
          return;
        }
      }
      // Process coordinates if not virtual
      let coordinates = null;
      let resolvedLocations = formData.locations || [];
      setLocationErrors({});
      
      if (!formData.isVirtual) {
        const nextErrors = {};
        const cleanedLocations = [];
        
        for (let i = 0; i < resolvedLocations.length; i++) {
          const locationEntry = resolvedLocations[i];
          const trimmedAddress = (locationEntry.address || '').trim();
          if (!trimmedAddress) {
            if (i === 0) {
              nextErrors[locationEntry.id] = 'Primary location is required.';
            }
            continue;
          }
          
          let entryCoordinates = locationEntry.coordinates;
          if (
            entryCoordinates &&
            typeof entryCoordinates.lat === 'number' &&
            typeof entryCoordinates.lng === 'number'
          ) {
            // keep as is
          } else {
            try {
              entryCoordinates = await geocodeAddress(trimmedAddress);
            } catch (geoError) {
              console.error("Geocoding error:", geoError);
              nextErrors[locationEntry.id] = "Couldn't verify this address. Try selecting a suggestion.";
            }
          }
          
          cleanedLocations.push({
            ...locationEntry,
            address: trimmedAddress,
            coordinates: entryCoordinates || null
          });
          
          if (!coordinates && entryCoordinates) {
            coordinates = entryCoordinates;
          }
        }
        
        if (cleanedLocations.length === 0) {
          nextErrors[
            resolvedLocations[0]?.id || 'primary'
          ] = 'Add at least one location.';
        }
        
        if (Object.keys(nextErrors).length > 0) {
          setLocationErrors(nextErrors);
          setGeocodingError("Please fix the highlighted locations before submitting.");
          setIsSubmitting(false);
          return;
        }
        
        resolvedLocations = cleanedLocations;
        if (!coordinates && resolvedLocations[0]?.coordinates) {
          coordinates = resolvedLocations[0].coordinates;
        }
      } else {
        resolvedLocations = [];
      }
      setGeocodingError(null);
      
      // Upload media files if provided
      let mediaData = [];
      
      if (mediaFiles.length > 0) {
        try {
          mediaData = await uploadMultipleMedia(mediaFiles);
        } catch (uploadErr) {
          console.error("Media upload error:", uploadErr);
          setUploadError(uploadErr.message || "Failed to upload media. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }
      
      // Add the listing with coordinates and media
      const listingId = await addListing(
        {
          ...formData,
          category: formData.category || 'other',
          location: formData.isVirtual
            ? formData.location
            : resolvedLocations[0]?.address || formData.location,
          coordinates,
          locations: resolvedLocations,
          media: mediaData,
          maxAttendees: formData.maxAttendees ? parseInt(formData.maxAttendees) : null,
          eventStart: startDate,
          eventEnd: endDate,
          // Keep imageUrl for backward compatibility (use first image if available)
          imageUrl: mediaData.find(m => m.type === 'image')?.url || ''
        }, 
        currentUser.uid
      );
      
      setIsSubmitting(false);
      alert("Listing created successfully!");
      navigate(`/listings/${listingId}`);
    } catch (error) {
      console.error("Error creating listing:", error);
      alert("Failed to create listing. Please try again.");
      setIsSubmitting(false);
    }
  };
  
  return (
    <div style={{ maxWidth: '600px', margin: '2rem auto', padding: '0 1rem' }}>
      <h1 style={{ color: 'var(--color-primary)' }}>Add New Listing</h1>
      <p>Create a new listing to share with the community.</p>
      
      <form onSubmit={handleSubmit} style={{ marginTop: '2rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="title" style={{ display: 'block', marginBottom: '0.5rem' }}>Title*</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>
        
        {/* Event Date/Time */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>When does the event occur? (Optional)</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label htmlFor="eventStart" style={{ display: 'block', marginBottom: '0.25rem' }}>Start</label>
              <input
                type="datetime-local"
                id="eventStart"
                name="eventStart"
                value={formData.eventStart}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
                <label htmlFor="eventEnd" style={{ display: 'block', marginBottom: '0.25rem' }}>End</label>
              <input
                type="datetime-local"
                id="eventEnd"
                name="eventEnd"
                value={formData.eventEnd}
                onChange={handleChange}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
          </div>
          <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
            Share as much scheduling detail as you have. Start and end times can be left blank.
          </small>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="description" style={{ display: 'block', marginBottom: '0.5rem' }}>Description*</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows="6"
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', whiteSpace: 'pre-wrap' }}
            placeholder="Describe your listing. Line breaks and spacing will be preserved in the display."
          ></textarea>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="category" style={{ display: 'block', marginBottom: '0.5rem' }}>Category (Optional)</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="">Select a category</option>
            <option value="education">Education</option>
            <option value="recreation">Recreation</option>
            <option value="childcare">Childcare</option>
            <option value="mentorship">Mentorship</option>
            <option value="professional_development">Professional Development</option>
            <option value="other">Other</option>
          </select>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              id="isVirtual"
              name="isVirtual"
              checked={formData.isVirtual}
              onChange={handleChange}
              style={{ marginRight: '0.5rem' }}
            />
            <label htmlFor="isVirtual">This is a virtual listing</label>
          </div>
        </div>
        
        {!formData.isVirtual && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              Locations*
            </label>
            <p style={{ marginTop: 0, marginBottom: '0.75rem', color: '#666', fontSize: '0.9rem' }}>
              Add one or more addresses. The first location is treated as the primary map pin.
            </p>
            {formData.locations.map((loc, index) => (
              <div 
                key={loc.id} 
                style={{ 
                  marginBottom: '0.75rem', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '6px', 
                  padding: '0.75rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                  <strong>Location {index + 1}</strong>
                  {index === 0 && <span style={{ fontSize: '0.8rem', color: '#999' }}>Primary</span>}
                </div>
                <LocationAutocomplete
                  id={`location-${loc.id}`}
                  name={`location-${loc.id}`}
                  value={loc.address}
                  onChange={(value) => handleLocationInputChange(loc.id, value)}
                  onSelect={(suggestion) => handleLocationSelect(loc.id, suggestion)}
                  required={index === 0}
                  disabled={isSubmitting}
                  placeholder="Enter a full address (123 Main St, City, Province/State, Country)"
                />
                {locationErrors[loc.id] && (
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#e74c3c' }}>
                    {locationErrors[loc.id]}
                  </small>
                )}
                {!locationErrors[loc.id] && loc.address && !loc.coordinates && (
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#666' }}>
                    Tip: select a suggestion to pin this address on the map.
                  </small>
                )}
                {!locationErrors[loc.id] && loc.coordinates && (
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#2ecc71' }}>
                    Coordinates saved ({loc.coordinates.lat.toFixed(4)}, {loc.coordinates.lng.toFixed(4)}).
                  </small>
                )}
                {formData.locations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveLocation(loc.id)}
                    style={{
                      marginTop: '0.5rem',
                      background: 'transparent',
                      border: 'none',
                      color: '#c0392b',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            {formData.locations.length < MAX_LOCATIONS && (
              <button
                type="button"
                onClick={handleAddLocation}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  backgroundColor: '#f1f5f9',
                  border: '1px dashed #b0bec5',
                  borderRadius: '4px',
                  padding: '0.4rem 0.75rem',
                  cursor: 'pointer'
                }}
              >
                + Add Another Location
              </button>
            )}
            {geocodingError && (
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#e74c3c' }}>
                {geocodingError}
              </small>
            )}
          </div>
        )}
        
        {formData.isVirtual && (
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="location" style={{ display: 'block', marginBottom: '0.5rem' }}>Virtual Location Details</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Zoom, Google Meet, Online Platform"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
        )}
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>Price Options*</label>
          
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              id="isFreeEvent"
              name="isFreeEvent"
              checked={formData.isFreeEvent}
              onChange={handleChange}
              style={{ marginRight: '0.5rem' }}
            />
            <label htmlFor="isFreeEvent">This is a free event</label>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              id="isContactForPrice"
              name="isContactForPrice"
              checked={formData.isContactForPrice}
              onChange={handleChange}
              style={{ marginRight: '0.5rem' }}
            />
            <label htmlFor="isContactForPrice">Contact for pricing</label>
          </div>
          
          {!formData.isFreeEvent && !formData.isContactForPrice && (
            <div>
              <label htmlFor="price" style={{ display: 'block', marginBottom: '0.5rem' }}>Price ($)</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                min="0"
                step="0.01"
                required={!formData.isFreeEvent && !formData.isContactForPrice}
                placeholder="0.00"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
          )}
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>Upload Media (Images/Videos)</label>
          <MultiMediaUpload 
            onMediaSelected={handleMediaSelected}
            existingMedia={[]}
          />
          {uploadError && (
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#e74c3c' }}>
              {uploadError}
            </small>
          )}
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="maxAttendees" style={{ display: 'block', marginBottom: '0.5rem' }}>Maximum Attendees (Optional)</label>
          <input
            type="number"
            id="maxAttendees"
            name="maxAttendees"
            value={formData.maxAttendees}
            onChange={handleChange}
            min="1"
            placeholder="Leave empty for unlimited"
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          />
          <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
            Leave empty if there's no attendance limit.
          </small>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="contact" style={{ display: 'block', marginBottom: '0.5rem' }}>Contact Email*</label>
          <input
            type="email"
            id="contact"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>
        
        {/* Social Media Section */}
        <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>Social Media & Website (Optional)</h3>

          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_website" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>Website</label>
            <input
              type="url"
              id="social_website"
              name="social_website"
              value={formData.socialMedia.website}
              onChange={handleChange}
              placeholder="https://www.example.com"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_facebook" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>Facebook</label>
            <input
              type="url"
              id="social_facebook"
              name="social_facebook"
              value={formData.socialMedia.facebook}
              onChange={handleChange}
              placeholder="https://www.facebook.com/yourpage"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_instagram" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>Instagram</label>
            <input
              type="text"
              id="social_instagram"
              name="social_instagram"
              value={formData.socialMedia.instagram}
              onChange={handleChange}
              placeholder="@yourusername or https://www.instagram.com/yourusername"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_twitter" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>Twitter/X</label>
            <input
              type="text"
              id="social_twitter"
              name="social_twitter"
              value={formData.socialMedia.twitter}
              onChange={handleChange}
              placeholder="@yourusername or https://twitter.com/yourusername"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_linkedin" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>LinkedIn</label>
            <input
              type="url"
              id="social_linkedin"
              name="social_linkedin"
              value={formData.socialMedia.linkedin}
              onChange={handleChange}
              placeholder="https://www.linkedin.com/in/yourprofile"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_youtube" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>YouTube</label>
            <input
              type="url"
              id="social_youtube"
              name="social_youtube"
              value={formData.socialMedia.youtube}
              onChange={handleChange}
              placeholder="https://www.youtube.com/channel/yourchannel"
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
        </div>

        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>* Required fields</p>
        
        <button 
          type="submit" 
          disabled={isSubmitting}
          style={{ 
            background: 'var(--color-primary)', 
            color: 'white', 
            border: 'none', 
            padding: '0.75rem 1.5rem', 
            borderRadius: '4px', 
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            marginTop: '1rem',
            opacity: isSubmitting ? 0.7 : 1
          }}
        >
          {isSubmitting ? 'Creating...' : 'Create Listing'}
        </button>
      </form>
    </div>
  );
}

export default AddListing;
